import { Box, IconButton, Paper, Portal, Stack, Typography } from "@mui/material";
import { Clear, Close, FilterAlt, PushPin, PushPinOutlined, Restore } from "@mui/icons-material";
import useFilters from "../../../hooks/useFilters";
import ActionButton, { FilterButtonWithoutEffect, LightGreyButton } from "../Buttons/ActionButton";
import MultiSelect from "../Selectors/MultiSelect";
import GroupedMultiSelect from "../Selectors/GroupedMultiSelect";
import SingleSelect from "../Selectors/SingleSelect";
import DisabledButton from "../Buttons/DisabledButton";
import CloseButton from "../Buttons/CloseButton";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HtmlTooltip, StyledDarkRedInformationTypography } from "../StyledComponents/styled";
import { DetailedSelectionIconWithTooltip } from "../../../api_calls/utils";
import StoreDetailTable from "../DetailedStore/StoreSelectionModal";
import { useDispatch, useSelector } from "react-redux";
import { deleteItems } from "../../../Store/actions";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import { checkUserDetailsFromCookie, checkUserRole } from "../../../api_calls/userManagement";
import { DROPDOWN_OPENED_EVENT } from "../Selectors/selectConstants";
import {
    ROOT_RETAILER_GROUP_PARAM,
    resolveLoggedInUserType,
    shouldUseRetailerStoreHierarchy,
} from "./storeFilterUtils";
import { readPinnedFilterKeys, writePinnedFilterKeys } from "./pinnedFiltersStorage";

const PIN_ICON_SIZE_PX = 18;
const UNPINNED_PIN_COLOR = "#62626E";
const PINNED_PIN_COLOR = "#AF3241";
const SUMMARY_LIMIT = 4;

const readUserEmail = () => {
    const details = checkUserDetailsFromCookie(false);
    const email = details?.success === true ? details.user_detail?.user_email : "";
    return typeof email === "string" && email !== "" ? email : "unknown";
};

const filterHasValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return value !== null && value !== undefined && value !== "";
};

const optionMatches = (option, value, selectionParam) => {
    if (typeof selectionParam !== "undefined") {
        return value === option[selectionParam];
    }
    return value === option.id || value === option.label || value === option.value;
};

const multiPrimitive = (item, selectionParam) => {
    if (typeof selectionParam !== "undefined") {
        return item[selectionParam];
    }
    if (typeof item.id !== "undefined") {
        return item.id;
    }
    if (typeof item.value !== "undefined") {
        return item.value;
    }
    return item.label;
};

// Single-select keeps the historical preference for id, then selectionParam.
const singlePrimitive = (item, selectionParam) => {
    if (typeof item.id !== "undefined") {
        return item.id;
    }
    if (typeof selectionParam !== "undefined") {
        return item[selectionParam];
    }
    if (typeof item.value !== "undefined") {
        return item.value;
    }
    return item.label;
};

const listedOptions = (filter) => {
    const listed = typeof filter.options === "function" ? filter.options() : [];
    return Array.isArray(listed) ? listed : [];
};

/**
 * Remote filters pass null until the fetch for this table load finishes,
 * which is what the selectors use to show a spinner. A failed fetch passes
 * an empty array so the open list can say there is nothing to show.
 */
const resolveFilterOptions = (filter, isFresh, isLoading, fetchFailed) => {
    if (typeof filter.loadOptions !== "function") {
        return listedOptions(filter);
    }
    if (isFresh === true) {
        return listedOptions(filter);
    }
    if (isLoading === true) {
        return null;
    }
    if (fetchFailed === true) {
        return [];
    }
    return null;
};

const sameKeyList = (left, right) => {
    if (left.length !== right.length) {
        return false;
    }
    return left.every((key, index) => key === right[index]);
};

/**
 * One filter control, used in the pinned row and again inside the pane.
 * `placement` is part of elemId so the two copies do not share a dropdown id.
 * Opening either copy still loads the same filter, because the listener
 * reads the key and ignores the placement.
 */
const FilterField = ({
    filter,
    draftValue,
    setDraftFilter,
    clearDraftFilter,
    resolvedOptions,
    fullWidth,
    elemId,
    useStoreGroups,
    onOpenDetailedStores,
}) => {
    const { key, label, type, selectionParam, disableSearch, showDetailedStoreSelection = false } = filter;
    const optionList = resolvedOptions === null ? [] : resolvedOptions;
    // While values are loading, pass null through so the open list shows a spinner.
    const optionsForSelect = resolvedOptions === null ? null : optionList;

    let defaultVal;
    if (type === "multi") {
        defaultVal = Array.isArray(draftValue)
            ? optionList.filter((option) => draftValue.some((value) => optionMatches(option, value, selectionParam)))
            : [];
    } else {
        defaultVal = draftValue
            ? optionList.find((option) => optionMatches(option, draftValue, selectionParam)) || null
            : null;
    }

    const detailedIcon = showDetailedStoreSelection === true
        ? (setOpen) => DetailedSelectionIconWithTooltip((event) => {
            event.stopPropagation();
            setOpen(false);
            onOpenDetailedStores();
        })
        : undefined;

    const onMultiChange = (selected) => {
        const newValues = selected.map((item) => multiPrimitive(item, selectionParam));
        const prev = draftValue || [];
        if (
            !Array.isArray(prev) ||
            newValues.length !== prev.length ||
            newValues.some((value, index) => value !== prev[index])
        ) {
            setDraftFilter(key, newValues);
        }
    };

    if (type === "multi" && useStoreGroups === true) {
        return (
            <GroupedMultiSelect
                label={label}
                elemId={elemId}
                enableFullWidth={fullWidth}
                selectionCheck={(option, value) => option.label === value.label}
                options={optionsForSelect}
                defaultVal={defaultVal}
                groupParam={ROOT_RETAILER_GROUP_PARAM}
                showGroupNames={true}
                showSelectionSummary={true}
                usedFor="filter"
                disableSearch={disableSearch}
                onChangeCall={onMultiChange}
                additionalIconWithTooltip={showDetailedStoreSelection}
                additionalIconMarkup={detailedIcon}
            />
        );
    }

    if (type === "multi") {
        return (
            <MultiSelect
                label={label}
                elemId={elemId}
                enableFullWidth={fullWidth}
                selectionCheck={(option, value) => option.label === value.label}
                options={optionsForSelect}
                defaultVal={defaultVal}
                usedFor="filter"
                disableSearch={disableSearch}
                onChangeCall={onMultiChange}
                additionalIconWithTooltip={showDetailedStoreSelection}
                additionalIconMarkup={detailedIcon}
            />
        );
    }

    return (
        <SingleSelect
            label={label}
            elemId={elemId}
            enableFullWidth={fullWidth}
            options={optionsForSelect}
            selectionCheck={(option, value) => value && option.label === value.label}
            defaultVal={defaultVal}
            usedFor="filter"
            disableSearch={disableSearch}
            onChangeCall={(selected) => {
                if (selected) {
                    const newVal = singlePrimitive(selected, selectionParam);
                    if (draftValue !== newVal) {
                        setDraftFilter(key, newVal);
                    }
                } else if (draftValue != null) {
                    clearDraftFilter(key);
                }
            }}
        />
    );
};

const PinButton = ({ pinned, label, onClick }) => (
    <HtmlTooltip title={pinned ? "Unpin from filter row" : "Pin to filter row"}>
        <IconButton
            size="small"
            aria-label={`${pinned ? "Unpin" : "Pin"} ${label}`}
            onClick={onClick}
        >
            {pinned ? (
                <PushPin sx={{ fontSize: PIN_ICON_SIZE_PX, color: PINNED_PIN_COLOR }} />
            ) : (
                <PushPinOutlined sx={{ fontSize: PIN_ICON_SIZE_PX, color: UNPINNED_PIN_COLOR, opacity: 0.5 }} />
            )}
        </IconButton>
    </HtmlTooltip>
);

const DisabledRowButton = ({ label, icon }) => (
    <Box sx={{ mt: 3, mb: 2, display: "inline-flex" }}>
        <DisabledButton
            onClick={(event) => {
                event.preventDefault();
            }}
            label={label}
            icon={icon}
        />
    </Box>
);

/**
 * Pinned filters sit above the table. The pane lists every filter and is
 * where pins are changed. Both edit the same draft. The table updates only
 * when the user clicks Apply or Clear All.
 *
 * `tableId` scopes the saved pins. `optionsVersion` changes after a listing
 * reload that follows an add or an update, which drops the dropdown cache.
 * `rightAction` is the page button (Overview) that stays at the right of
 * the button row. `loadOptions` on a filter config fetches that filter only.
 */
const FilterPanel = ({
    filtersConfig,
    applyFilterFunc,
    clearFilterFunc,
    initiallyAppliedVals,
    tableId,
    optionsVersion = 0,
    rightAction = null,
    draftKeyForStore,
}) => {
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = useState(false);
    const paneRef = useRef(null);
    const storeDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const loggedInUserType = resolveLoggedInUserType();
    const isBrandUser = storeDetails?.brand_report_view === true || checkUserRole()?.user_role === "Brand";
    const userEmail = useMemo(() => readUserEmail(), []);
    // null: the user has never pinned or unpinned on this table, so the first three apply.
    const [customPins, setCustomPins] = useState(() => readPinnedFilterKeys(userEmail, tableId));

    const {
        draftFilters,
        appliedFilters,
        appliedCount,
        hasUnsavedChanges,
        open,
        openDrawer,
        closeDrawer,
        setDraftFilter,
        clearDraftFilter,
        resetDraftFilters,
        applyFilters,
        clearAppliedFilters,
    } = useFilters({
        config: filtersConfig,
        initialApplied: initiallyAppliedVals,
        onApply: (filters) => {
            applyFilterFunc(filters);
        },
        onClear: () => {
            clearFilterFunc();
        },
    });

    // closeDrawer is a new function every render. The outside-click effect
    // reads it through a ref so the listener is not torn down on each render.
    const closeDrawerRef = useRef(closeDrawer);
    closeDrawerRef.current = closeDrawer;

    const enabledFilters = useMemo(
        () => filtersConfig.filter((filter) => filter.enabled !== false),
        [filtersConfig]
    );

    const pinnedKeys = useMemo(() => {
        const enabledKeys = enabledFilters.map((filter) => filter.key);
        if (customPins === null) {
            return enabledKeys.slice(0, 3);
        }
        // Pane order, not the order the user clicked the pins.
        return enabledKeys.filter((key) => customPins.includes(key));
    }, [customPins, enabledFilters]);

    useEffect(() => {
        if (customPins === null) {
            return;
        }
        const enabledKeys = enabledFilters.map((filter) => filter.key);
        const removed = customPins.some((key) => enabledKeys.includes(key) === false);
        if (removed !== true) {
            return;
        }
        const pruned = enabledKeys.filter((key) => customPins.includes(key));
        if (sameKeyList(pruned, customPins)) {
            return;
        }
        setCustomPins(pruned);
        writePinnedFilterKeys(userEmail, tableId, pruned);
    }, [customPins, enabledFilters, tableId, userEmail]);

    const togglePin = (key) => {
        const enabledKeys = enabledFilters.map((filter) => filter.key);
        const next = pinnedKeys.includes(key)
            ? pinnedKeys.filter((pinnedKey) => pinnedKey !== key)
            : enabledKeys.filter((enabledKey) => enabledKey === key || pinnedKeys.includes(enabledKey));
        setCustomPins(next);
        writePinnedFilterKeys(userEmail, tableId, next);
    };

    // Cache is "this key was fetched for this table version". Refs avoid
    // starting a second request when the open event fires twice.
    const configRef = useRef(filtersConfig);
    configRef.current = filtersConfig;
    const optionsVersionRef = useRef(optionsVersion);
    optionsVersionRef.current = optionsVersion;
    const loadedVersionByKey = useRef({});
    const failedVersionByKey = useRef({});
    const loadingVersionByKey = useRef({});
    const [loadingVersionByKeyState, setLoadingVersionByKeyState] = useState({});

    const ensureLoaded = useCallback((key) => {
        const filter = configRef.current.find((item) => item.key === key && item.enabled !== false);
        if (!filter || typeof filter.loadOptions !== "function") {
            return;
        }
        const version = optionsVersionRef.current;
        if (version < 1) {
            return;
        }
        if (loadedVersionByKey.current[key] === version) {
            return;
        }
        if (loadingVersionByKey.current[key] === version) {
            return;
        }
        loadingVersionByKey.current = { ...loadingVersionByKey.current, [key]: version };
        setLoadingVersionByKeyState(loadingVersionByKey.current);
        const startedAt = version;
        Promise.resolve(filter.loadOptions())
            .then((result) => {
                if (optionsVersionRef.current !== startedAt) {
                    return;
                }
                if (result && result.success === false) {
                    failedVersionByKey.current = { ...failedVersionByKey.current, [key]: startedAt };
                    return;
                }
                loadedVersionByKey.current = { ...loadedVersionByKey.current, [key]: startedAt };
                if (failedVersionByKey.current[key] === startedAt) {
                    const nextFailed = { ...failedVersionByKey.current };
                    delete nextFailed[key];
                    failedVersionByKey.current = nextFailed;
                }
            })
            .catch(() => {
                if (optionsVersionRef.current === startedAt) {
                    failedVersionByKey.current = { ...failedVersionByKey.current, [key]: startedAt };
                }
            })
            .finally(() => {
                if (loadingVersionByKey.current[key] !== startedAt) {
                    return;
                }
                const nextLoading = { ...loadingVersionByKey.current };
                delete nextLoading[key];
                loadingVersionByKey.current = nextLoading;
                setLoadingVersionByKeyState(nextLoading);
            });
    }, []);

    // The selectors already announce the open. elemId is `${tableId}:${key}:row|pane`.
    useEffect(() => {
        const onDropdownOpened = (event) => {
            const openedId = event.detail?.id ?? event.detail;
            if (typeof openedId !== "string") {
                return;
            }
            const prefix = `${tableId}:`;
            if (openedId.startsWith(prefix) !== true) {
                return;
            }
            const rest = openedId.slice(prefix.length);
            const splitAt = rest.lastIndexOf(":");
            if (splitAt <= 0) {
                return;
            }
            ensureLoaded(rest.slice(0, splitAt));
        };
        window.addEventListener(DROPDOWN_OPENED_EVENT, onDropdownOpened);
        return () => window.removeEventListener(DROPDOWN_OPENED_EVENT, onDropdownOpened);
    }, [ensureLoaded, tableId]);

    // Applied values load with the page. A pinned dropdown has to show the
    // selection while it is closed, and Apply still needs the loaded rows
    // (duplicate visual names, store numbers). A filter with no value waits
    // until its dropdown opens.
    useEffect(() => {
        if (optionsVersion < 1) {
            return;
        }
        enabledFilters.forEach((filter) => {
            if (filterHasValue(appliedFilters[filter.key])) {
                ensureLoaded(filter.key);
            }
        });
    }, [appliedFilters, enabledFilters, ensureLoaded, optionsVersion]);

    useEffect(() => {
        let shouldApplyResult = true;
        if (loggedInUserType === "") {
            setRetailerHierarchyAvailable(false);
            return undefined;
        }
        loadRetailerAccessAvailability(loggedInUserType)
            .then((retailerAccessInfo) => {
                if (shouldApplyResult) {
                    setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
                }
            })
            .catch(() => {
                if (shouldApplyResult) {
                    setRetailerHierarchyAvailable(false);
                }
            });
        return () => {
            shouldApplyResult = false;
        };
    }, [loggedInUserType]);

    // The pane is not a modal: no backdrop, no scroll lock, no focus trap.
    // A click outside closes it and is left alone, so it still sorts a column
    // or presses a button. The listener waits one tick so the click that
    // opened the pane cannot close it again on the way to document.
    useEffect(() => {
        if (open !== true) {
            return undefined;
        }
        const onClick = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            if (target.closest("[data-filter-pane-toggle]")) {
                return;
            }
            // Pin and unpin swap the icon before this click reaches document.
            // The old icon is no longer in the pane, so contains() would close it.
            if (target.isConnected === false) {
                return;
            }
            if (paneRef.current && paneRef.current.contains(target)) {
                return;
            }
            // Dropdown lists and the store picker are portaled outside the pane.
            if (target.closest("[role='listbox']") || target.closest(".MuiPopper-root") || target.closest(".MuiDialog-root")) {
                return;
            }
            closeDrawerRef.current();
        };
        const onKeyDown = (event) => {
            if (event.key !== "Escape") {
                return;
            }
            // Escape closes the open dropdown first. The list is still mounted
            // on this tick, so the pane stays open until a second Escape.
            if (document.querySelector("[role='listbox']")) {
                return;
            }
            closeDrawerRef.current();
        };
        const timer = window.setTimeout(() => {
            document.addEventListener("click", onClick);
            document.addEventListener("keydown", onKeyDown);
        }, 0);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener("click", onClick);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const optionsFor = (filter) => resolveFilterOptions(
        filter,
        loadedVersionByKey.current[filter.key] === optionsVersion,
        loadingVersionByKeyState[filter.key] === optionsVersion,
        failedVersionByKey.current[filter.key] === optionsVersion
    );

    const groupsStores = (filter, resolvedOptions) => {
        if (filter.groupStoresByRetailer !== true || resolvedOptions === null) {
            return false;
        }
        return shouldUseRetailerStoreHierarchy({
            isBrand: isBrandUser,
            retailerHierarchyAvailable,
            storeOptions: resolvedOptions,
        });
    };

    const unpinnedSummary = useMemo(() => {
        const list = [];
        enabledFilters.forEach((filter) => {
            if (pinnedKeys.includes(filter.key)) {
                return;
            }
            const value = appliedFilters[filter.key];
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    list.push(`${filter.label} (${value.length})`);
                }
                return;
            }
            if (!filterHasValue(value)) {
                return;
            }
            const match = listedOptions(filter).find((option) => optionMatches(option, value, filter.selectionParam));
            list.push(`${filter.label} (${match ? match.label : value})`);
        });
        return list;
    }, [appliedFilters, enabledFilters, pinnedKeys]);

    const summaryLabel = pinnedKeys.length > 0 ? "Also filtered by:" : "Filtered by:";
    const allFiltersLabel = <>All Filters {appliedCount > 0 ? `(${appliedCount})` : ""}</>;

    const renderField = (filter, placement, fullWidth) => {
        const resolvedOptions = optionsFor(filter);
        return (
            <FilterField
                filter={filter}
                draftValue={draftFilters[filter.key]}
                setDraftFilter={setDraftFilter}
                clearDraftFilter={clearDraftFilter}
                resolvedOptions={resolvedOptions}
                fullWidth={fullWidth}
                elemId={`${tableId}:${filter.key}:${placement}`}
                useStoreGroups={groupsStores(filter, resolvedOptions)}
                onOpenDetailedStores={() => {
                    ensureLoaded(filter.key);
                    setShowSelectionModal(true);
                }}
            />
        );
    };

    return (
        <>
            <Box>
                {pinnedKeys.length > 0 && (
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        {enabledFilters.filter((filter) => pinnedKeys.includes(filter.key)).map((filter) => (
                            <Box key={filter.key} sx={{ width: 220, maxWidth: "100%" }}>
                                {renderField(filter, "row", false)}
                            </Box>
                        ))}
                    </Stack>
                )}
                <Stack direction="row" spacing={2} alignItems="flex-end" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        {hasUnsavedChanges ? (
                            <ActionButton onClick={applyFilters} label="Apply" icon={<FilterAlt />} />
                        ) : (
                            <DisabledRowButton label="Apply" icon={<FilterAlt />} />
                        )}
                        {appliedCount > 0 ? (
                            <LightGreyButton onClick={clearAppliedFilters} label="Clear All" icon={<Clear />} />
                        ) : (
                            <DisabledRowButton label="Clear All" icon={<Clear />} />
                        )}
                        <Box component="span" data-filter-pane-toggle="" sx={{ display: "inline-flex" }}>
                            <FilterButtonWithoutEffect
                                onClick={(event) => {
                                    // Stop the click before it reaches document. The outside-click
                                    // listener treats anything outside the pane as "close".
                                    event.stopPropagation();
                                    openDrawer();
                                }}
                                label={allFiltersLabel}
                                icon={<FilterAlt />}
                            />
                        </Box>
                        {unpinnedSummary.length > 0 && (
                            <StyledDarkRedInformationTypography
                                variant="body2"
                                data-filter-pane-toggle=""
                                onClick={(event) => {
                                    event.stopPropagation();
                                    openDrawer();
                                }}
                                sx={{ cursor: "pointer", fontWeight: "normal", mt: 3, mb: 2 }}
                            >
                                {summaryLabel} {unpinnedSummary.slice(0, SUMMARY_LIMIT).join(", ")}
                                {unpinnedSummary.length > SUMMARY_LIMIT && `, and + ${unpinnedSummary.length - SUMMARY_LIMIT} more`}
                            </StyledDarkRedInformationTypography>
                        )}
                        {open !== true && hasUnsavedChanges && (
                            <StyledDarkRedInformationTypography variant="body2" sx={{ fontWeight: "normal", mt: 3, mb: 2 }}>
                                Changes not applied.
                            </StyledDarkRedInformationTypography>
                        )}
                    </Stack>
                    {rightAction != null && (
                        <Box sx={{ display: "flex", pb: 2, "& .MuiButton-root": { mt: 0, mb: 0 } }}>
                            {rightAction}
                        </Box>
                    )}
                </Stack>
            </Box>

            {enabledFilters.length !== 0 && open && (
                <Portal>
                    {/* Fixed panel on the document body. A temporary Drawer is a full-screen
                        modal, and the click that opened it was also closing it. */}
                    <Paper
                        ref={paneRef}
                        role="dialog"
                        aria-modal="false"
                        aria-label="Filters"
                        elevation={16}
                        sx={{
                            position: "fixed",
                            top: 0,
                            right: 0,
                            height: "100%",
                            width: 420,
                            maxWidth: "100%",
                            zIndex: (theme) => theme.zIndex.modal,
                            display: "flex",
                            flexDirection: "column",
                            overflowY: "auto",
                            p: 2,
                            boxSizing: "border-box",
                        }}
                    >
                        <Typography variant="h6">Filters</Typography>
                        <Typography variant="body2" sx={{ color: UNPINNED_PIN_COLOR }}>
                            Pin a filter to show it in the row above the table.
                        </Typography>
                        {enabledFilters.map((filter) => (
                            <Stack
                                key={filter.key}
                                direction="row"
                                alignItems="flex-end"
                                spacing={1}
                                sx={{ mt: 2 }}
                            >
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    {renderField(filter, "pane", true)}
                                </Box>
                                <PinButton
                                    pinned={pinnedKeys.includes(filter.key)}
                                    label={filter.label}
                                    onClick={() => togglePin(filter.key)}
                                />
                            </Stack>
                        ))}
                        <Box sx={{ mt: "auto" }}>
                            {hasUnsavedChanges && (
                                <StyledDarkRedInformationTypography variant="body2" sx={{ mb: 2, fontWeight: "normal" }}>
                                    Changes not applied.
                                </StyledDarkRedInformationTypography>
                            )}
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-end"
                                width="100%"
                                spacing={1}
                            >
                                <CloseButton
                                    onClick={() => {
                                        closeDrawer();
                                    }}
                                    label="Close"
                                    icon={<Close />}
                                />
                                <Stack direction="row" spacing={1} alignItems="flex-end">
                                    {hasUnsavedChanges ? (
                                        <ActionButton onClick={resetDraftFilters} label="Reset selections" icon={<Restore />} />
                                    ) : (
                                        <DisabledButton
                                            onClick={(event) => {
                                                event.preventDefault();
                                            }}
                                            label="Reset selections"
                                            icon={<Restore />}
                                        />
                                    )}
                                    {hasUnsavedChanges ? (
                                        <ActionButton onClick={applyFilters} label="Apply" icon={<FilterAlt />} />
                                    ) : (
                                        <DisabledButton
                                            onClick={(event) => {
                                                event.preventDefault();
                                            }}
                                            label="Apply"
                                            icon={<FilterAlt />}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        </Box>
                    </Paper>
                    {showSelectionModal && (
                        <StoreDetailTable
                            open={showSelectionModal}
                            onClose={() => {
                                setShowSelectionModal(false);
                                dispatchAction(deleteItems(["store_selection_pagination", "store_list_filter_applied", "detailed_stores_filtered"]));
                            }}
                            applyChangesOnSelect={false}
                            selectedValues={draftFilters[draftKeyForStore] || []}
                            toHandleApplyChanges={(selectedStoreIds) => {
                                // Update the draft only. Apply is what reloads the table.
                                setDraftFilter(draftKeyForStore, selectedStoreIds);
                            }}
                            storeOptionsList={storeDetails.store_list_response_for_filters}
                        />
                    )}
                </Portal>
            )}
        </>
    );
};

export default FilterPanel;
